import React, { Fragment, useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react'
import 'primereact/resources/themes/lara-light-cyan/theme.css'
import { Toast } from 'primereact/toast'
import Modal from 'react-bootstrap/Modal'
import { API_URL } from '../../../utils/Constants/URLS'
import { useNavigate } from 'react-router-dom'

const FocProductRequirement = forwardRef((props, ref) => {
    const { id, onDataUpdate } = props
    const toast = useRef(null)
    const navigate = useNavigate()
    const token = localStorage.getItem('token')
    const [modalMessage, setModalMessage] = useState('')
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [focLeadDetails, setFocLeadDetails] = useState(null)
    const [isCompleted, setIsCompleted] = useState(false)

    // Form states
    const [typeOfSale, setTypeOfSale] = useState('')
    const [natureOfSale, setNatureOfSale] = useState('')
    const [leadSource, setLeadSource] = useState('')
    const [description, setDescription] = useState('')
    const [proposedDealAmount, setProposedDealAmount] = useState(0)
    const [sourceName, setSourceName] = useState('')
    const [enquiryDetails, setEnquiryDetails] = useState('')

    // Expose saveData method for Save All functionality
    useImperativeHandle(ref, () => ({
        saveData: async () => {
            // Skip if already completed
            if (isCompleted) {
                return { success: true, skipped: true, stage: 'productRequirements' }
            }

            // Save current form data with pending status (no validation required for Save All)
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        productRequirements: {
                            typeOfSale,
                            natureOfSale,
                            enquiryDetails,
                            leadSource,
                            proposedDealAmount,
                            description,
                            sourceName,
                            status: 'done'
                        }
                    })
                })
                const response = await res.json()
                return { success: response.error === false, stage: 'productRequirements' }
            } catch (error) {
                console.error('Error saving product requirements:', error)
                return { success: false, stage: 'productRequirements', error }
            }
        },
        isCompleted: () => isCompleted
    }))

    // Sync with parent focLeadDetails prop
    useEffect(() => {
        if (props.focLeadDetails) {
            const leadData = props.focLeadDetails
            setFocLeadDetails(leadData)

            // Check if this stage is completed
            if (leadData?.productRequirements?.status === 'done') {
                setIsCompleted(true)
            }

            // Prefill form logic - only if fields are empty to avoid overwriting user edits
            const pr = leadData?.productRequirements || leadData?.originalLeadId?.productRequirements

            if (pr) {
                if (pr.typeOfSale && typeOfSale === '') setTypeOfSale(pr.typeOfSale)
                if (pr.natureOfSale && natureOfSale === '') setNatureOfSale(pr.natureOfSale)
                if (pr.leadSource && leadSource === '') setLeadSource(pr.leadSource)
                if (pr.description && description === '') setDescription(pr.description)
                if (pr.proposedDealAmount && proposedDealAmount === 0) setProposedDealAmount(pr.proposedDealAmount)
                if (pr.sourceName && sourceName === '') setSourceName(pr.sourceName)
                if (pr.enquiryDetails && enquiryDetails === '') setEnquiryDetails(pr.enquiryDetails)
            }
        }
    }, [props.focLeadDetails]) // Added form states to dependencies to ensure pre-fill only if empty

    // Fetch FOC Lead Details (Initial fallback)
    useEffect(() => {
        const fetchFocLead = async () => {
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const response = await res.json()
                const leadData = response.data || response
                setFocLeadDetails(leadData)

                // Check if this stage is completed
                if (leadData?.productRequirements?.status === 'done') {
                    setIsCompleted(true)
                }

                // Prefill form: use FOC lead data if exists, otherwise use original lead data
                // Priority: FOC lead's productRequirements > originalLeadId's productRequirements
                const pr = leadData?.productRequirements || leadData?.originalLeadId?.productRequirements

                if (pr) {
                    if (pr.typeOfSale) setTypeOfSale(pr.typeOfSale)
                    if (pr.natureOfSale) setNatureOfSale(pr.natureOfSale)
                    if (pr.leadSource) setLeadSource(pr.leadSource)
                    if (pr.description) setDescription(pr.description)
                    if (pr.proposedDealAmount) setProposedDealAmount(pr.proposedDealAmount)
                    if (pr.sourceName) setSourceName(pr.sourceName)
                    if (pr.enquiryDetails) setEnquiryDetails(pr.enquiryDetails)
                }
            } catch (e) {
                console.error('Error fetching FOC lead:', e)
            }
        }

        if (id && !props.focLeadDetails) { // Only fetch if id is present and prop is not provided
            fetchFocLead()
        }
    }, [id, token, props.focLeadDetails]) // Added props.focLeadDetails to dependencies

    const handleCloseModal = () => {
        setShowSuccessModal(false)
        setShowDeclineModal(false)
        // Use callback to refresh parent data instead of full page reload
        if (onDataUpdate) {
            onDataUpdate()
        } else {
            window.location.reload()
        }
    }

    const updateProductRequirement = async e => {
        e.preventDefault()

        // Check if at least one line item exists
        const lineItems = focLeadDetails?.productRequirements?.crossSells || []
        const activeLineItems = lineItems.filter(item => item.status !== 'deleted')

        if (activeLineItems.length === 0) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please add at least one line item before submitting Product Requirement',
                life: 4000
            })
            return
        }

        if (typeOfSale === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Type of Sale',
                life: 3000
            })
        } else if (natureOfSale === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select Nature of Sale',
                life: 3000
            })
        } else if (leadSource === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Select a Proper Lead Source',
                life: 3000
            })
        } else if (leadSource === 'Others' && sourceName === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter the Lead Source Details',
                life: 3000
            })
        } else if (enquiryDetails === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter a Proper Enquiry Details',
                life: 3000
            })
        } else if (description === '') {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Please Enter a Proper Description',
                life: 3000
            })
        } else {
            try {
                const res = await fetch(`${API_URL}foc/lead/${id}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: JSON.stringify({
                        productRequirements: {
                            typeOfSale,
                            natureOfSale,
                            enquiryDetails,
                            leadSource,
                            proposedDealAmount,
                            description,
                            sourceName,
                            status: 'done'
                        }
                    })
                })
                const response = await res.json()
                if (response.error === false) {
                    setModalMessage('Product Requirement Updated Successfully')
                    setShowSuccessModal(true)
                } else {
                    setModalMessage('Something Went Wrong')
                    setShowDeclineModal(true)
                }
            } catch (e) {
                setModalMessage('Oops! Unable to add data')
                setShowDeclineModal(true)
            }
        }
    }

    return (
        <Fragment>
            <div>
                {isCompleted ? (
                    // Completed View - Read Only Display
                    <div className='completed-stage-view'>
                        <div className='row g-3'>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Type of Sale</label>
                                    <p className='mb-0 fs-6'>{typeOfSale || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Nature of Sale</label>
                                    <p className='mb-0 fs-6'>{natureOfSale || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Lead Source</label>
                                    <p className='mb-0 fs-6'>
                                        {leadSource || '-'}
                                        {leadSource === 'Others' && sourceName && ` (${sourceName})`}
                                    </p>
                                </div>
                            </div>
                            <div className='col-md-6'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Enquiry Details</label>
                                    <p className='mb-0 fs-6'>{enquiryDetails || '-'}</p>
                                </div>
                            </div>
                            <div className='col-md-12'>
                                <div className='mb-3'>
                                    <label className='form-label text-muted fw-semibold'>Description</label>
                                    <p className='mb-0 fs-6'>{description || '-'}</p>
                                </div>
                            </div>
                        </div>
                        <div className='d-flex align-items-center mt-2'>
                            <span className='badge bg-success'>
                                <i className='bi bi-check-circle me-1'></i> Completed
                            </span>
                        </div>
                    </div>
                ) : (
                    // Form View - Editable
                    <form className='row g-3 needs-validation'>
                        <div className='row ms-1 p-3'>
                            <div className='col-md-6'>
                                <label htmlFor='name' className='form-label'>
                                    Type of Sale <span className='text-danger'>*</span>
                                </label>
                                <select
                                    name='typeOfSale'
                                    required
                                    value={typeOfSale}
                                    onChange={e => setTypeOfSale(e.target.value)}
                                    className='form-select'
                                >
                                    <option value=''>Select Type</option>
                                    <option value='Domestic'>Domestic</option>
                                    <option value='Export'>Export</option>
                                </select>
                            </div>
                            <div className='col-md-6'>
                                <label htmlFor='name' className='form-label'>
                                    Nature of Sale <span className='text-danger'>*</span>
                                </label>
                                <select
                                    name='natureOfSale'
                                    required
                                    value={natureOfSale}
                                    onChange={e => setNatureOfSale(e.target.value)}
                                    className='form-select'
                                >
                                    <option value=''>Select Type</option>
                                    <option value='Product'>Product</option>
                                    <option value='Project'>Project</option>
                                </select>
                            </div>
                        </div>

                        <div className='row ms-1 p-3'>
                            <div className='col-md-12'>
                                <label htmlFor='lead-source' className='form-label'>
                                    Lead Source <span className='text-danger'>*</span>
                                </label>
                                <select
                                    id='leadSource'
                                    className='form-select'
                                    value={leadSource}
                                    onChange={e => setLeadSource(e.target.value)}
                                    required
                                >
                                    <option value=''>Select</option>
                                    <option value='Direct Inquiry'>Direct Inquiry</option>
                                    <option value='Existing Customer Referral'>
                                        Existing Customer Referral
                                    </option>
                                    <option value='Customer Visit / Site Visit'>
                                        Customer Visit / Site Visit
                                    </option>
                                    <option value='Trade Show / Exhibition'>
                                        Trade Show / Exhibition
                                    </option>
                                    <option value='Industry Seminar / Workshop'>
                                        Industry Seminar / Workshop
                                    </option>
                                    <option value='Dealer / Channel Partner'>
                                        Dealer / Channel Partner
                                    </option>
                                    <option value='Company Website'>Company Website</option>
                                    <option value='Online Advertisement'>
                                        Online Advertisement
                                    </option>
                                    <option value='Social Media'>Social Media</option>
                                    <option value='Email Campaign'>Email Campaign</option>
                                    <option value='Cold Call / Telecalling'>
                                        Cold Call / Telecalling
                                    </option>
                                    <option value='Inbound Phone Inquiry'>
                                        Inbound Phone Inquiry
                                    </option>
                                    <option value='Walk In'>Walk In</option>
                                    <option value='WhatsApp / SMS Campaign'>
                                        WhatsApp / SMS Campaign
                                    </option>
                                    <option value='Tenders / Bids / RFPs'>
                                        Tenders / Bids / RFPs
                                    </option>
                                    <option value='Service Engineer Referral'>
                                        Service Engineer Referral
                                    </option>
                                    <option value='Sales Engineer Referral'>
                                        Sales Engineer Referral
                                    </option>
                                    <option value='Repeat Customer (New Requirement)'>
                                        Repeat Customer (New Requirement)
                                    </option>
                                    <option value='Others'>Others (Please Specify)</option>
                                </select>
                            </div>
                            {leadSource === 'Others' && (
                                <div className='col-md-12 mt-2'>
                                    <label className='form-label'>
                                        Source Details <span className='text-danger'>*</span>
                                    </label>
                                    <input
                                        name='sourceName'
                                        type='text'
                                        className='form-control'
                                        value={sourceName}
                                        onChange={e => setSourceName(e.target.value)}
                                        required
                                    />
                                </div>
                            )}
                        </div>

                        <Fragment>
                            <div className='row ms-1 p-3'>
                                <div className='col-md-6'>
                                    <label htmlFor='enquiry-details' className='form-label'>
                                        Enquiry Details <span className='text-danger'>*</span>
                                    </label>
                                    <textarea
                                        name='enquiryDetails'
                                        type='text'
                                        className='form-control'
                                        value={enquiryDetails}
                                        onChange={e => setEnquiryDetails(e.target.value)}
                                        required
                                    />
                                </div>
                                <div className='col-md-6'>
                                    <label htmlFor='description' className='form-label'>
                                        Description <span className='text-danger'>*</span>
                                    </label>
                                    <textarea
                                        name='description'
                                        type='text'
                                        className='form-control'
                                        value={description}
                                        onChange={e => setDescription(e.target.value)}
                                        required
                                    />
                                </div>
                            </div>

                            <div className='col-md-12 d-flex justify-content-end'>
                                <button
                                    onClick={updateProductRequirement}
                                    className='btn add-btn text-white'
                                    type='button'
                                >
                                    Save
                                </button>
                            </div>
                        </Fragment>
                    </form>
                )}

                {/* Success & Error Modals */}
                <Modal show={showSuccessModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 shrink-0 fill-green-500 inline'
                                viewBox='0 0 512 512'
                            >
                                <path d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z' />
                                <path d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z' />
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Good Job
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>

                <Modal show={showDeclineModal} centered>
                    <Modal.Body>
                        <div className='my-8 text-center'>
                            <svg
                                xmlns='http://www.w3.org/2000/svg'
                                className='w-14 fill-red-500 inline'
                                viewBox='0 0 286.054 286.054'
                            >
                                <path
                                    fill='#e2574c'
                                    d='M143.027 0C64.04 0 0 64.04 0 143.027c0 78.996 64.04 143.027 143.027 143.027 78.996 0 143.027-64.022 143.027-143.027C286.054 64.04 222.022 0 143.027 0zm0 259.236c-64.183 0-116.209-52.026-116.209-116.209S78.844 26.818 143.027 26.818s116.209 52.026 116.209 116.209-52.026 116.209-116.209 116.209zm.009-196.51c-10.244 0-17.995 5.346-17.995 13.981v79.201c0 8.644 7.75 13.972 17.995 13.972 9.994 0 17.995-5.551 17.995-13.972V76.707c-.001-8.43-8.001-13.981-17.995-13.981zm0 124.997c-9.842 0-17.852 8.01-17.852 17.86 0 9.833 8.01 17.843 17.852 17.843s17.843-8.01 17.843-17.843c-.001-9.851-8.001-17.86-17.843-17.86z'
                                ></path>
                            </svg>
                            <h4 className='text-xl text-gray-800 font-semibold mt-4'>
                                Error Occured
                            </h4>
                            <p className='text-sm text-gray-500 leading-relaxed mt-4'>
                                {modalMessage}!
                            </p>
                        </div>

                        <button
                            type='button'
                            onClick={handleCloseModal}
                            className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-red-500 hover:bg-red-700'
                        >
                            Okay
                        </button>
                    </Modal.Body>
                </Modal>
                <Toast ref={toast} />
            </div>
        </Fragment>
    )
})

export default FocProductRequirement
